import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useCreateQuotationMutation, useUpdateQuotationMutation, useGetQuotationQuery } from '../../../store/api/quotationsApi';
import { useLazyGetCustomersQuery } from '../../../store/api/salesApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { usePermission } from '../../../hooks/usePermission';
import ProductFormContent from '../../products/pages/ProductFormContent';
import { CustomerFormContent } from '../../sales/pages/CustomerForm';
import { getLocalDateString } from '../../../shared/utils/dateUtils';

const DEFAULT_VAT = 5;

function QuotationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';

  const { hasPermission, isAdmin } = usePermission();
  const canWholesale = isAdmin || hasPermission('sales.orders.wholesale_sale');

  const { data: quotation, isLoading: loadingQ } = useGetQuotationQuery(id, { skip: !isEdit });
  const [createQuotation] = useCreateQuotationMutation();
  const [updateQuotation] = useUpdateQuotationMutation();
  const [triggerCustomerSearch, { data: customerSearchData, isFetching: searchingCustomers }] = useLazyGetCustomersQuery();
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const isTaxEnabled = company?.taxLabel !== 'None' && Number(company?.vatRate ?? 0) > 0;
  const defaultVat = company?.taxLabel === 'None' ? 0 : Number(company?.vatRate ?? 0);

  const [formData, setFormData] = useState({
    customerId: '',
    quotationDate: getLocalDateString(),
    validUntil: '',
    notes: '',
    salesPersonId: '',
    locationId: '',
    roundOff: 0,
  });
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  useEffect(() => {
    if (quotation && isEdit) {
      setFormData({
        customerId: quotation.customerId || '',
        quotationDate: quotation.quotationDate ? quotation.quotationDate.substring(0, 10) : '',
        validUntil: quotation.validUntil ? quotation.validUntil.substring(0, 10) : '',
        notes: quotation.notes || '',
        salesPersonId: quotation.salesPersonId || '',
        locationId: quotation.locationId || '',
        roundOff: quotation.roundOff || 0,
      });
      setItems(
        (quotation.items || []).map((it) => ({
          productId: it.productId,
          productName: it.product?.name,
          quantity: it.quantity,
          unit: it.unit || 'pcs',
          unitPrice: it.unitPrice,
          discount: Number(it.discount) || 0,
          vatPercent: Number(it.vatPercent) || defaultVat,
        }))
      );
    }
  }, [quotation, isEdit]);

  useEffect(() => {
    if (customerSearchData?.data) {
      setCustomerSearchOptions(customerSearchData.data);
    } else if (Array.isArray(customerSearchData)) {
      setCustomerSearchOptions(customerSearchData);
    }
  }, [customerSearchData]);

  useEffect(() => {
    if (quotation?.customer && !customerSearchOptions.find(c => c.id === quotation.customer.id)) {
      setCustomerSearchOptions(prev => [quotation.customer, ...prev]);
    }
  }, [quotation, customerSearchOptions]);

  const selectedCustomer = customerSearchOptions.find((c) => c.id === formData.customerId) || null;

  useEffect(() => {
    if (!isEdit && locations?.length && !formData.locationId) {
      const mainWarehouse = locations.find((l) => l.name.toLowerCase().includes('main warehouse'));
      if (mainWarehouse) {
        setFormData((prev) => ({ ...prev, locationId: mainWarehouse.id }));
      } else if (locations.length > 0) {
        setFormData((prev) => ({ ...prev, locationId: locations[0].id }));
      }
    }
  }, [locations, isEdit]);


  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const computeLine = (item) => {
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      vatAmount = vatIncluded ? lineSubtotal - lineSubtotal / (1 + vatPct / 100) : lineSubtotal * (vatPct / 100);
    }
    const lineTotal = vatIncluded ? lineSubtotal : lineSubtotal + vatAmount;
    return { vatAmount: Math.round((vatAmount) * Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) / Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), lineTotal: Math.round((lineTotal) * Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) / Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2) };
  };

  const [pricingMode, setPricingMode] = useState('retail'); // 'retail' | 'wholesale'

  const handlePricingModeChange = (newMode) => {
    if (newMode === 'wholesale' && !canWholesale) return;
    setPricingMode(newMode);
    if (items.length > 0) {
      setItems((prev) =>
        prev.map((item) => {
          let basePrice = 0;
          if (newMode === 'wholesale') {
            basePrice = (item.wholesalePrice != null && Number(item.wholesalePrice) > 0)
              ? Number(item.wholesalePrice)
              : Number(item.sellingPrice != null ? item.sellingPrice : item.unitPrice || 0);
          } else {
            basePrice = Number(item.sellingPrice != null ? item.sellingPrice : item.unitPrice || 0);
          }
          return {
            ...item,
            unitPrice: basePrice,
            pricingType: newMode,
          };
        })
      );
    }
  };

  const orderVatTotal = items.reduce((sum, item) => sum + computeLine(item).vatAmount, 0);
  const orderSubtotal = items.reduce((sum, item) => sum + toNum(item.quantity) * toNum(item.unitPrice), 0);
  const orderDiscountTotal = items.reduce((sum, item) => sum + toNum(item.discount), 0);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = vatIncluded ? netOrder : netOrder + orderVatTotal;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;

  const handleAddProduct = (product) => {
    if (!product) return;
    const basePrice =
      pricingMode === 'wholesale' && product.wholesalePrice != null && Number(product.wholesalePrice) > 0
        ? Number(product.wholesalePrice)
        : Number(product.sellingPrice ?? 0);
    setItems((prev) => [
      {
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unit: 'pcs',
        sellingPrice: product.sellingPrice ?? 0,
        wholesalePrice: product.wholesalePrice ?? 0,
        pricingType:
          pricingMode === 'wholesale' && product.wholesalePrice != null && Number(product.wholesalePrice) > 0
            ? 'wholesale'
            : 'retail',
        unitPrice: basePrice,
        discount: 0,
        vatPercent: defaultVat,
      },
      ...prev,
    ]);
    setProductSearch('');
  };

  const handleRemoveItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId) {
      alert('Customer is required');
      return;
    }
    if (!formData.quotationDate) {
      alert('Quotation date is required');
      return;
    }
    if (items.length === 0 || items.some((i) => !i.productId)) {
      alert('Add at least one item');
      return;
    }

    const payload = {
      ...formData,
      quotationDate: formData.quotationDate,
      validUntil: formData.validUntil || undefined,
      roundOff: Number(formData.roundOff) || 0,
      items: items.map((item) => {
        const { vatAmount, lineTotal } = computeLine(item);
        return {
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unit: item.unit || undefined,
          unitPrice: parseFloat(item.unitPrice),
          discount: toNum(item.discount),
          vatPercent: toNum(item.vatPercent) || defaultVat,
          vatAmount,
          total: lineTotal,
        };
      }),
    };

    try {
      if (isEdit) {
        await updateQuotation({ id, ...payload }).unwrap();
        alert('Quotation updated');
        navigate(`/quotations/${id}`);
      } else {
        const created = await createQuotation(payload).unwrap();
        alert('Quotation created');
        navigate(`/quotations/${created.id}`);
      }
    } catch (err) {
      alert(err?.data?.message || 'Error saving quotation');
    }
  };

  if (isEdit && loadingQ) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(isEdit ? `/quotations/${id}` : '/quotations')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Quotation' : 'New Quotation'}
        </Typography>
      </Box>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth
                options={customerSearchOptions}
                loading={searchingCustomers}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionDisabled={(option) => !canWholesale && option?.customerType === 'wholesale'}
                value={selectedCustomer}
                onChange={(_, newValue) => {
                  if (newValue?.customerType === 'wholesale' && !canWholesale) {
                    return;
                  }
                  setFormData({ ...formData, customerId: newValue?.id || '' });
                  if (newValue?.customerType === 'wholesale' && canWholesale) {
                    handlePricingModeChange('wholesale');
                  } else if (newValue?.customerType === 'retail' && pricingMode === 'wholesale') {
                    handlePricingModeChange('retail');
                  }
                }}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  const isRestrictedWholesale = !canWholesale && option.customerType === 'wholesale';
                  return (
                    <li key={key || option.id} {...otherProps} style={{ opacity: isRestrictedWholesale ? 0.5 : 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{option.name}</span>
                        {option.customerType === 'wholesale' && (
                          <Chip
                            size="small"
                            label={canWholesale ? "Wholesale" : "Wholesale (Restricted)"}
                            color={canWholesale ? "secondary" : "default"}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, ml: 1 }}
                          />
                        )}
                      </Box>
                    </li>
                  );
                }}
                onInputChange={(_, newInputValue) => {
                  if (newInputValue && newInputValue.length >= 2) {
                    triggerCustomerSearch({ search: newInputValue, limit: 20 });
                  } else if (!newInputValue) {
                    triggerCustomerSearch({ limit: 20 });
                  }
                }}
                onOpen={() => {
                  if (customerSearchOptions.length === 0) triggerCustomerSearch({ limit: 20 });
                }}
                renderInput={(params) => <TextField {...params} label="Customer *" required={!formData.customerId} />}
              />
              <Tooltip title="Add New Customer">
                <Button
                  variant="outlined"
                  onClick={() => setCustomerDialogOpen(true)}
                  sx={{ height: 56, minWidth: 48, px: 1.5, borderRadius: 1.5, whiteSpace: 'nowrap' }}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Tooltip>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Quotation Date *"
              value={formData.quotationDate}
              onChange={(e) => setFormData({ ...formData, quotationDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Valid Until"
              value={formData.validUntil}
              onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              select
              label="Sales Person"
              value={formData.salesPersonId}
              onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {userList.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              select
              label="Location"
              value={formData.locationId}
              onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {(locations || []).map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>{loc.name} ({loc.code})</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Items</Typography>
                {canWholesale && (
                  <ToggleButtonGroup
                    value={pricingMode}
                    exclusive
                    onChange={(e, newMode) => {
                      if (newMode) handlePricingModeChange(newMode);
                    }}
                    size="small"
                    sx={{
                      height: 32,
                      '& .MuiToggleButton-root': {
                        px: 1.5,
                        py: 0.2,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.8rem',
                      },
                      '& .Mui-selected': {
                        bgcolor: pricingMode === 'wholesale' ? '#6C5CE7 !important' : '#1976D2 !important',
                        color: '#fff !important',
                      },
                    }}
                  >
                    <ToggleButton value="retail">
                      🏷️ Retail
                    </ToggleButton>
                    <ToggleButton value="wholesale">
                      🏢 Wholesale
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, maxWidth: 500 }}>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<AddIcon />}
                onClick={() => setProductDialogOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                New Product
              </Button>
              <Autocomplete
                fullWidth
                options={productSearchData?.products || []}
                loading={searchingProducts}
                value={null}
                inputValue={productSearch}
                getOptionLabel={(opt) => opt?.name || ''}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  const isWholesale = pricingMode === 'wholesale';
                  return (
                    <li key={key || option.id} {...otherProps}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 0.5 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {option.sku || '-'} | Stock: {option.stock ?? 0}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: isWholesale ? 400 : 700,
                              color: isWholesale ? 'text.secondary' : 'primary.main',
                            }}
                          >
                            RT: {currency} {Number(option.sellingPrice || 0).toFixed(company?.currencyDecimals ?? 2)}
                          </Typography>
                          {canWholesale && Number(option.wholesalePrice) > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontWeight: isWholesale ? 700 : 400,
                                color: isWholesale ? 'secondary.main' : 'text.secondary',
                              }}
                            >
                              WS: {currency} {Number(option.wholesalePrice || 0).toFixed(company?.currencyDecimals ?? 2)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </li>
                  );
                }}
                onInputChange={(_, v) => {
                  setProductSearch(v);
                  if (v && v.length >= 2) triggerProductSearch({ search: v, limit: 20 });
                }}
                onChange={(_, v) => {
                  if (v) {
                    const typed = String(productSearch).trim();
                    if (typed && v.barcode !== typed && v.sku !== typed && !v.name.toLowerCase().includes(typed.toLowerCase())) {
                      return;
                    }
                    handleAddProduct(v);
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Search product" placeholder="Type to search" />}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                    {isTaxEnabled && <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => {
                    const { lineTotal } = computeLine(item);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{item.productName || '-'}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const current = Number(item.quantity || 0) || 0;
                                const next = Math.max(0.01, current - 1);
                                handleItemChange(idx, 'quantity', String(next));
                              }}
                              title="Decrease"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              required
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const current = Number(item.quantity || 0) || 0;
                                const next = Math.max(0.01, current + 1);
                                handleItemChange(idx, 'quantity', String(next));
                              }}
                              title="Increase"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(idx, 'unitPrice', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 100 }}
                            />
                            {canWholesale && Number(item.wholesalePrice) > 0 && (
                              <Tooltip
                                title={
                                  item.pricingType === 'wholesale'
                                    ? `Wholesale Active. Click to switch to Retail (${currency} ${Number(item.sellingPrice || 0).toFixed(company?.currencyDecimals ?? 2)})`
                                    : `Retail Active. Click to switch to Wholesale (${currency} ${Number(item.wholesalePrice || 0).toFixed(company?.currencyDecimals ?? 2)})`
                                }
                              >
                                <Chip
                                  size="small"
                                  label={item.pricingType === 'wholesale' ? 'WS' : 'RT'}
                                  color={item.pricingType === 'wholesale' ? 'secondary' : 'default'}
                                  variant={item.pricingType === 'wholesale' ? 'filled' : 'outlined'}
                                  onClick={() => {
                                    const nextType = item.pricingType === 'wholesale' ? 'retail' : 'wholesale';
                                    const targetBase =
                                      nextType === 'wholesale'
                                        ? Number(item.wholesalePrice)
                                        : Number(item.sellingPrice || 0);
                                    setItems((prev) => {
                                      const updated = [...prev];
                                      updated[idx] = {
                                        ...updated[idx],
                                        unitPrice: targetBase,
                                        pricingType: nextType,
                                      };
                                      return updated;
                                    });
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: '0.68rem',
                                    height: 24,
                                    px: 0.5,
                                    userSelect: 'none',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.discount}
                            onChange={(e) => handleItemChange(idx, 'discount', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        {isTaxEnabled && (
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.vatPercent}
                              onChange={(e) => handleItemChange(idx, 'vatPercent', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                        )}
                        <TableCell>{new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(lineTotal)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleRemoveItem(idx)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6">
                Grand Total: {new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(grandTotal)}
              </Typography>
              <TextField
                size="small"
                type="number"
                label="Round Off"
                value={formData.roundOff}
                onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                inputProps={{ step: 0.01 }}
                sx={{ width: 120 }}
              />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Total: {new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(totalAmount)}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Button type="submit" variant="contained" startIcon={<SaveIcon />} sx={{ borderRadius: 2 }}>
              {isEdit ? 'Update Quotation' : 'Create Quotation'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Product</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 0, overflow: 'visible', '& .MuiInputLabel-root': { overflow: 'visible' } }}>
          <ProductFormContent
            compact
            onCancel={() => setProductDialogOpen(false)}
            onSuccess={(created) => {
              setProductDialogOpen(false);
              if (created?.id) {
                handleAddProduct(created);
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add New Customer</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 0, overflow: 'visible', '& .MuiInputLabel-root': { overflow: 'visible' } }}>
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              if (created?.id) {
                setCustomerSearchOptions((prev) => [created, ...prev.filter((c) => c.id !== created.id)]);
                setFormData((prev) => ({ ...prev, customerId: created.id }));
                if (created.customerType === 'wholesale' && canWholesale) {
                  handlePricingModeChange('wholesale');
                } else if (created.customerType === 'retail' && pricingMode === 'wholesale') {
                  handlePricingModeChange('retail');
                }
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default QuotationForm;
